import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ROLE_OPTIONS as roles } from "@/constants/roles";

import { makeWorkerShiftHistoryQuery } from "../-queries";

function labelForRole(roleName: string): string {
  const match = roles.find(
    (r) => r.value === roleName || r.label === roleName,
  );
  return match ? match.label : roleName;
}

function formatShiftDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function formatShiftRange(startIso: string, endIso: string): string {
  const start = formatShiftDate(startIso);
  const end = formatShiftDate(endIso);
  if (start === end) return start;
  return `${start} – ${end}`;
}

type Assignment = {
  id: number;
  start_date_time: string;
  end_date_time: string;
  assignment_status: string;
  shift: {
    role_name?: string | null;
    business: { name?: string | null };
  };
};

type ExperienceRolesProps = {
  workerId: string;
  roles?: ReadonlyArray<string>;
};

export function ExperienceRoles({ workerId, roles: rolesPerformed }: ExperienceRolesProps) {
  const { t } = useTranslation("profile");
  const [openRole, setOpenRole] = useState<string | null>(null);

  // History is only needed to populate the per-role dialog details.
  const { data, isLoading } = useQuery({
    ...makeWorkerShiftHistoryQuery(workerId),
    enabled: openRole !== null,
  });

  const shiftsForRole = useMemo(() => {
    if (!openRole) return [];
    const assignments = (data?.assignments ?? []) as unknown as Array<Assignment>;
    return assignments
      .filter(
        (a) =>
          a.assignment_status === "COMPLETED" &&
          a.shift.role_name === openRole,
      )
      .sort(
        (a, b) =>
          new Date(b.start_date_time).getTime() -
          new Date(a.start_date_time).getTime(),
      );
  }, [data?.assignments, openRole]);

  const performed = rolesPerformed ?? [];

  return (
    <div className="w-full mt-4">
      <div className="font-semibold text-sm mb-1">{t("worker.experience")}</div>
      {performed.length === 0 ? (
        <div className="text-xs text-muted-foreground">
          {t("worker.noRolesPerformed")}
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {performed.map((roleValue) => (
            <button
              key={roleValue}
              type="button"
              onClick={() => setOpenRole(roleValue)}
              className="bg-fog text-garnet rounded-md px-3 py-1 text-xs font-medium border border-ember/30 hover:bg-ember/10 transition cursor-pointer"
            >
              {labelForRole(roleValue)}
            </button>
          ))}
        </div>
      )}

      <Dialog
        open={openRole !== null}
        onOpenChange={(o) => !o && setOpenRole(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("worker.shiftHistoryTitle", {
                role: openRole ? labelForRole(openRole) : "",
              })}
            </DialogTitle>
          </DialogHeader>
          {isLoading ? (
            <div className="text-sm text-muted-foreground">
              {t("worker.shiftHistoryLoading")}
            </div>
          ) : shiftsForRole.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              {t("worker.shiftHistoryEmpty")}
            </div>
          ) : (
            <ul className="flex flex-col gap-3 max-h-[60vh] overflow-y-auto">
              {shiftsForRole.map((a) => (
                <li
                  key={a.id}
                  className="border rounded-md p-3 bg-fog/50"
                >
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-ember inline-block" />
                    <span className="font-semibold text-sm">
                      {a.shift.business.name ?? "—"}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground ml-4 mt-0.5">
                    {formatShiftRange(a.start_date_time, a.end_date_time)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
